import { Actions, AppContext, Events } from "@Context/common/general/context";
import { AnalyticsEvents } from "@Enums";
import { NavigationEvent } from "@Globals/types";
import * as Sentry from "@sentry/react";
import { FC, useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

type NavigationData = {
  [key: string]: {
    data?: any;
  };
};

interface INavigationService {}

// Hoisted variables to avoid closures in useEffect hooks
let currentPath: string = "";
let forwardPath: string = "";
let lastPath: string = "";
let history: string[] = [];
let navigationData: NavigationData = {};

const NavigationService: FC<INavigationService> = ({}) => {
  const { state, dispatch } = useContext(AppContext);
  const location = useLocation();
  const navigateHook = useNavigate();

  const isWeb = !state.device.isUnityEmbedded!;

  const navigate = (path: string, options: any) => {
    navigateHook(path, options);
    Sentry.addBreadcrumb({
      category: "Navigation",
      type: "navigate",
      message: "Navigated to path",
      data: { path, options },
    });
  };

  /**
   * ANIMATION
   */
  const handleAnimate = (path: string, delay?: number, callback = () => {}) => {
    if (delay) {
      setTimeout(() => {
        handleAnimate(path, 0, callback);
      }, delay);
      return;
    }

    Events.app.animatePage.emit({
      path: path,
      onAnimateComplete: callback,
    });
  };

  /**
   * FORWARD NAVIGATION
   */
  const prepNavigation = ({ path, data, replace, reset, resetTo }: NavigationEvent) => {
    const shouldReplace = replace || path === currentPath;

    lastPath = currentPath;
    forwardPath = path;

    // Remove last path if it should be replaced
    if (shouldReplace) {
      history.pop();
    }

    // Add path to history if its not the same as the last path
    if (history[history.length - 1] !== path) {
      history.push(path);
    }

    // Reset history to a specific path
    if (resetTo) {
      const resetIndex = history.findIndex((p) => p === resetTo);
      if (resetIndex > -1) {
        history = [...history.slice(0, resetIndex + 1), path];
        lastPath = resetTo;
      }
    }

    // Reset history with only the current path
    if (reset) {
      history = [path];
      lastPath = "/";
    }

    if (state.user.authenticated) {
      dispatch({
        type: Actions.AppActions.AnalyticsEvent,
        payload: {
          type: AnalyticsEvents.page_navigated,
          data: {
            page: forwardPath.replace(/\/[0-9]+/g, ""),
          },
        },
      });
    }

    // Add nav data to state
    if (data) navigationData[forwardPath] = { data };

    return shouldReplace;
  };

  const onNavigate = ({ path, data, delay, replace, reset, resetTo, onNavigated }: NavigationEvent) => {
    const shouldReplace = prepNavigation({ path, data, replace, reset, resetTo });
    const navigateToPath = () => {
      navigate(forwardPath, {
        state: {
          from: lastPath,
          ...(data || {}),
        },
        replace: shouldReplace,
      });
      onNavigated?.();
    };

    if (!isWeb) {
      handleAnimate(forwardPath, delay, navigateToPath);
    } else {
      navigateToPath();
    }
  };

  const onNavigateExclusive = ({ path, data, replace }: NavigationEvent) => {
    const shouldReplace = prepNavigation({ path, data, replace });
    navigate(forwardPath, { state: data || {}, replace: shouldReplace });
  };

  /**
   * BACKWARD NAVIGATION
   */
  const prepNavigateBack = (steps: number) => {
    lastPath = currentPath;
    if (steps > 1) {
      history.splice(history.length - steps, steps);
    } else {
      history.pop();
    }

    if (history.length === 0) {
      history = ["/"];
    }

    forwardPath = history[history.length - 1];
  };

  const navigateBack = () => {
    // Get data for next page and remove current page data
    const data = navigationData[forwardPath]?.data;
    delete navigationData[currentPath];
    navigate(forwardPath, {
      state: {
        from: lastPath !== "/" ? lastPath : undefined,
        ...(data || {}),
      },
      replace: true,
    });
  };

  const onNavigateBack = (steps = 1) => {
    prepNavigateBack(steps);
    handleAnimate(forwardPath, 0, navigateBack);
  };

  const onNavigateBackExclusive = (steps = 1) => {
    prepNavigateBack(steps);
    navigateBack();
  };

  useEffect(() => {
    state.app.events.navigate.uniq(onNavigate);
    state.app.events.navigateExclusive.uniq(onNavigateExclusive);
    state.app.events.navigateBack.uniq(onNavigateBack);
    state.app.events.navigateBackExclusive.uniq(onNavigateBackExclusive);
    return () => {
      state.app.events.navigate.remove(onNavigate);
      state.app.events.navigateExclusive.remove(onNavigateExclusive);
      state.app.events.navigateBack.remove(onNavigateBack);
      state.app.events.navigateBackExclusive.remove(onNavigateBackExclusive);
    };
  }, [state.user.authenticated]);

  useEffect(() => {
    currentPath = location.pathname;
    // Add root to history if its empty
    if (history.length === 0) {
      if (location.pathname !== "/") {
        history.push("/");
      }
      history.push(location.pathname);
    }
  }, [location]);

  return null;
};

export default NavigationService;
